import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './VillageDetailModal.css';
import { FaTimes, FaTint, FaChartLine, FaChartBar, FaCalendar, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import DetailedPredictionAnalysis from './DetailedPredictionAnalysis';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const VillageDetailModal = ({ village, onClose, userState: propUserState, userDistrict: propUserDistrict }) => {
  const [historyData, setHistoryData] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  const [error, setError] = useState(null);
  const [showPrediction, setShowPrediction] = useState(false);
  const [showPredictionInfo, setShowPredictionInfo] = useState(false);
  const [userState, setUserState] = useState(propUserState || null);
  const [userDistrict, setUserDistrict] = useState(propUserDistrict || null);
  const [userRole, setUserRole] = useState(null);

  // Get user's data from localStorage if not provided as props
  useEffect(() => {
    if (propUserState) {
      setUserState(propUserState);
    }
    if (propUserDistrict) {
      setUserDistrict(propUserDistrict);
    }
    
    const userData = localStorage.getItem('user');
    const isLoggedIn = localStorage.getItem('loggedIn');
    if (userData && isLoggedIn) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'farmer');
        
        // Set state for all users if not provided as prop
        if (!propUserState && user.selectedState) {
          setUserState(user.selectedState);
        }
        
        // Set district if not provided as prop
        if (!propUserDistrict && user.selectedDistrict) {
          setUserDistrict(user.selectedDistrict);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, [propUserState, propUserDistrict]);

  useEffect(() => {
    // Fetch data if we have village information (use village's location, not user's)
    if (village && village.state && village.district) {
      fetchVillageHistory();
      fetchPrediction(); // Auto-load prediction
    }
  }, [village]);

  const fetchVillageHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the village's own state and district information
      const stateToUse = village.state;
      const districtToUse = village.district;
      
      console.log('Fetching history for:', stateToUse, districtToUse, village.village);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/villages/history/${encodeURIComponent(stateToUse)}/${encodeURIComponent(districtToUse)}/${encodeURIComponent(village.village)}`
      );
      
      const result = await response.json();
      
      console.log('API Response:', result);
      
      if (result.success) {
        console.log('History data loaded:', result.data);
        setHistoryData(result.data);
      } else {
        console.error('API Error:', result.error);
        setError(result.error || 'Failed to load history');
      }
    } catch (err) {
      console.error('Error fetching village history:', err);
      setError('Failed to load village history');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrediction = async () => {
    try {
      setLoadingPrediction(true);
      
      // Use the village's own state and district information
      const stateToUse = village.state;
      const districtToUse = village.district;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/villages/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          state: stateToUse,
          district: districtToUse,
          villageName: village.village,
          latitude: village.latitude,
          longitude: village.longitude
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setPredictionData(result.data);
        setShowPrediction(true);
      } else {
        console.error('Prediction error:', result.error);
      }
    } catch (err) {
      console.error('Error fetching prediction:', err);
    } finally {
      setLoadingPrediction(false);
    }
  };

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('loggedIn') === 'true';
  
  if (!isLoggedIn) {
    return (
      <section className="village-inline-container">
        <div className="village-modal-content">
          <div className="village-modal-error">
            <FaExclamationTriangle />
            <p>Please log in to view village details</p>
            <button 
              onClick={() => {
                window.location.href = '/';
              }}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </section>
    );
  }

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'significantly_improving':
        return '🟢';
      case 'improving':
        return '✅';
      case 'stable':
        return '➖';
      case 'worsening':
        return '⚠️';
      case 'significantly_worsening':
        return '🔴';
      default:
        return '❓';
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'significantly_improving':
        return 'Significantly Improving';
      case 'improving':
        return 'Improving';
      case 'stable':
        return 'Stable';
      case 'worsening':
        return 'Worsening';
      case 'significantly_worsening':
        return 'Significantly Worsening';
      default:
        return 'Unknown';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'significantly_improving':
        return '#0d7a0d';
      case 'improving':
        return '#4caf50';
      case 'stable':
        return '#9e9e9e';
      case 'worsening':
        return '#ff9800';
      case 'significantly_worsening':
        return '#d32f2f';
      default:
        return '#666';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Safe': '#1fc21f',
      'Semi Critical': '#f9c802',
      'Critical': '#ff8d1a',
      'Over Exploited': '#ea2328',
      'Unknown': '#e6e6e6'
    };
    return colors[category] || '#e6e6e6';
  };

  // Prepare chart data
  const chartData = historyData && historyData.history && historyData.history.length > 0 ? {
    labels: historyData.history.map(h => h.year),
    datasets: [
      {
        label: 'Water Level (m)',
        data: historyData.history.map(h => h.waterLevel),
        borderColor: getTrendColor(historyData.trend.direction),
        backgroundColor: getTrendColor(historyData.trend.direction) + '20',
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: historyData.history.map(h => getCategoryColor(h.category)),
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: '4-Year Water Level Trend',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const dataPoint = historyData.history[context.dataIndex];
            return [
              `Water Level: ${dataPoint.waterLevel}m`,
              `Category: ${dataPoint.category}`,
              `Date: ${dataPoint.date}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Water Level (meters)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <section className="village-inline-container">
      <div className="village-modal-content">
        {/* Header */}
        <div className="village-modal-header">
          <div className="village-modal-title">
            <FaTint className="village-modal-icon" />
            <div>
              <h2>{village.village}</h2>
              <p className="village-modal-location">
                {village.district}, {village.state}
              </p>
            </div>
          </div>
          {onClose && (
            <button className="village-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="village-modal-body">
          {loading ? (
            <div className="village-modal-loading">
              <div className="loading-spinner"></div>
              <p>Loading historical data...</p>
            </div>
          ) : error ? (
            <div className="village-modal-error">
              <FaExclamationTriangle />
              <p>{error}</p>
            </div>
          ) : historyData ? (
            <>
              {/* Current Status */}
              <div className="village-current-status">
                <div className="status-card">
                  <FaTint className="status-icon" />
                  <div className="status-info">
                    <span className="status-label">Current Water Level (2024)</span>
                    <span className="status-value">
                      {historyData.history.length > 0 
                        ? historyData.history[historyData.history.length - 1].waterLevel 
                        : village.waterLevel}m
                    </span>
                  </div>
                </div>
                <div className="status-card">
                  <div 
                    className="status-category-badge"
                    style={{ backgroundColor: getCategoryColor(
                      historyData.history.length > 0 
                        ? historyData.history[historyData.history.length - 1].category 
                        : village.category
                    ) }}
                  >
                    {historyData.history.length > 0 
                      ? historyData.history[historyData.history.length - 1].category 
                      : village.category}
                  </div>
                </div>
                <div className="status-card">
                  <FaCalendar className="status-icon" />
                  <div className="status-info">
                    <span className="status-label">Last Updated</span>
                    <span className="status-value">
                      {historyData.history.length > 0 
                        ? historyData.history[historyData.history.length - 1].date 
                        : village.date}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trend Summary */}
              <div 
                className="village-trend-summary"
                style={{ borderLeftColor: getTrendColor(historyData.trend.direction) }}
              >
                <div className="trend-header">
                  <span className="trend-icon">{getTrendIcon(historyData.trend.direction)}</span>
                  <h3 style={{ color: getTrendColor(historyData.trend.direction) }}>
                    {getTrendLabel(historyData.trend.direction)}
                  </h3>
                </div>
                <div className="trend-stats">
                  <div className="trend-stat">
                    <span className="trend-stat-label">Change ({historyData.trend.yearsSpan})</span>
                    <span 
                      className="trend-stat-value"
                      style={{ color: getTrendColor(historyData.trend.direction) }}
                    >
                      {historyData.trend.change > 0 ? '+' : ''}{historyData.trend.change}m
                    </span>
                  </div>
                  <div className="trend-stat">
                    <span className="trend-stat-label">Percentage Change</span>
                    <span 
                      className="trend-stat-value"
                      style={{ color: getTrendColor(historyData.trend.direction) }}
                    >
                      {historyData.trend.percentChange > 0 ? '+' : ''}{historyData.trend.percentChange}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              {chartData ? (
                <div className="village-chart-container">
                  <Line data={chartData} options={chartOptions} />
                </div>
              ) : (
                <div className="village-chart-container" style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  color: '#666'
                }}>
                  <p>📊 Chart data not available</p>
                  <p style={{ fontSize: '14px', margin: '10px 0 0 0' }}>
                    Historical data may be incomplete for this village
                  </p>
                </div>
              )}

              {/* Year-by-Year Table */}
              {historyData && historyData.history && historyData.history.length > 0 && (
                <div className="village-history-table">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0 }}><FaChartBar /> Year-by-Year Comparison</h3>
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#666', 
                    fontStyle: 'italic',
                    backgroundColor: '#f0f4ff',
                    padding: '4px 10px',
                    borderRadius: '12px'
                  }}>
                    * Rainfall & Extraction: District-level data
                  </span>
                </div>
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Water Depth</th>
                        <th>Rainfall (mm)</th>
                        <th>Extraction Ratio</th>
                        <th>Category</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.history.map((item, index) => {
                        const prevItem = index > 0 ? historyData.history[index - 1] : null;
                        const depthChange = prevItem ? item.waterLevel - prevItem.waterLevel : null;
                        const rainfallChange = prevItem && item.rainfall && prevItem.rainfall 
                          ? item.rainfall - prevItem.rainfall : null;
                        
                        return (
                          <tr key={item.year}>
                            <td><strong>{item.year}</strong></td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                  {item.waterLevel}m
                                </span>
                                {depthChange !== null && (
                                  <span style={{ 
                                    fontSize: '12px',
                                    color: depthChange > 0 ? '#d32f2f' : depthChange < 0 ? '#2e7d32' : '#666',
                                    fontWeight: '500'
                                  }}>
                                    ({depthChange > 0 ? '+' : ''}{depthChange.toFixed(2)}m)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {item.rainfall ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ fontSize: '15px', fontWeight: '500' }}>
                                    {item.rainfall.toFixed(1)} mm
                                  </span>
                                  {rainfallChange !== null && (
                                    <span style={{ 
                                      fontSize: '11px',
                                      color: rainfallChange > 0 ? '#2e7d32' : rainfallChange < 0 ? '#d32f2f' : '#666'
                                    }}>
                                      {rainfallChange > 0 ? '↑' : rainfallChange < 0 ? '↓' : '→'} 
                                      {Math.abs(rainfallChange).toFixed(0)} mm
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>—</span>
                              )}
                            </td>
                            <td>
                              {item.extraction ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ 
                                    fontSize: '15px', 
                                    fontWeight: '500',
                                    color: item.extraction.ratio >= 90 ? '#d32f2f' : 
                                           item.extraction.ratio >= 70 ? '#f57c00' : '#2e7d32'
                                  }}>
                                    {item.extraction.ratio}%
                                  </span>
                                  <span style={{ fontSize: '10px', color: '#666' }}>
                                    {item.extraction.ratio >= 100 ? 'Over-Exploited' :
                                     item.extraction.ratio >= 90 ? 'Critical' :
                                     item.extraction.ratio >= 70 ? 'Semi-Critical' : 'Safe'}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>—</span>
                              )}
                            </td>
                            <td>
                              <span 
                                className="table-category-badge"
                                style={{ backgroundColor: getCategoryColor(item.category) }}
                              >
                                {item.category}
                              </span>
                            </td>
                            <td>
                              {depthChange !== null ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ 
                                    color: depthChange > 0 ? '#d32f2f' : depthChange < 0 ? '#2e7d32' : '#666',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                  }}>
                                    {depthChange > 0 ? '⬆️ Worsening' : depthChange < 0 ? '⬇️ Improving' : '➡️ Stable'}
                                  </span>
                                  {item.rainfall && prevItem && prevItem.rainfall && item.extraction && prevItem.extraction && (
                                    <span style={{ 
                                      fontSize: '11px',
                                      color: '#666',
                                      fontStyle: 'italic'
                                    }}>
                                      {(() => {
                                        const extractionChange = parseFloat(item.extraction.ratio) - parseFloat(prevItem.extraction.ratio);
                                        const isOverExploited = parseFloat(item.extraction.ratio) >= 100;
                                        
                                        // If depth worsening despite more rain
                                        if (depthChange > 0 && rainfallChange > 0) {
                                          if (isOverExploited) {
                                            return '⚠️ Over-extraction';
                                          } else if (extractionChange > 5) {
                                            return '⚠️ High extraction';
                                          } else {
                                            return 'Despite more rain';
                                          }
                                        }
                                        // If depth improving with more rain
                                        else if (depthChange < 0 && rainfallChange > 0) {
                                          return '✓ Rain helping';
                                        }
                                        // If depth worsening with less rain
                                        else if (depthChange > 0 && rainfallChange < 0) {
                                          return 'Low rainfall';
                                        }
                                        // If depth improving with less rain
                                        else if (depthChange < 0 && rainfallChange < 0) {
                                          return '✓ Better management';
                                        }
                                        // Default
                                        else {
                                          return rainfallChange > 0 ? 'More rain' : rainfallChange < 0 ? 'Less rain' : 'Same rain';
                                        }
                                      })()}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Summary Stats */}
                <div className="year-comparison-summary">
                  <div className="summary-card">
                    <div className="summary-icon">💧</div>
                    <div className="summary-content">
                      <div className="summary-label">Avg Water Depth</div>
                      <div className="summary-value">
                        {(historyData.history.reduce((sum, h) => sum + h.waterLevel, 0) / historyData.history.length).toFixed(2)}m
                      </div>
                    </div>
                  </div>
                  
                  {historyData.history.some(h => h.rainfall) && (
                    <div className="summary-card">
                      <div className="summary-icon">🌧️</div>
                      <div className="summary-content">
                        <div className="summary-label">Avg Rainfall</div>
                        <div className="summary-value">
                          {(historyData.history
                            .filter(h => h.rainfall)
                            .reduce((sum, h) => sum + h.rainfall, 0) / 
                            historyData.history.filter(h => h.rainfall).length
                          ).toFixed(0)} mm
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {historyData.history.some(h => h.extraction) && (
                    <div className="summary-card">
                      <div className="summary-icon">📊</div>
                      <div className="summary-content">
                        <div className="summary-label">Avg Extraction</div>
                        <div className="summary-value">
                          {(historyData.history
                            .filter(h => h.extraction)
                            .reduce((sum, h) => sum + parseFloat(h.extraction.ratio), 0) / 
                            historyData.history.filter(h => h.extraction).length
                          ).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="summary-card">
                    <div className="summary-icon">
                      {historyData.trend.direction.includes('worsening') ? '⚠️' : 
                       historyData.trend.direction.includes('improving') ? '✅' : '➡️'}
                    </div>
                    <div className="summary-content">
                      <div className="summary-label">Overall Trend</div>
                      <div className="summary-value" style={{
                        color: historyData.trend.direction.includes('worsening') ? '#d32f2f' :
                               historyData.trend.direction.includes('improving') ? '#2e7d32' : '#666'
                      }}>
                        {historyData.trend.direction.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Recommendations */}
              {historyData && historyData.trend && (
              <div className="village-recommendations">
                <h3><FaChartLine /> Insights & Recommendations</h3>
                {historyData.trend.direction === 'significantly_worsening' || historyData.trend.direction === 'worsening' ? (
                  <div className="recommendation-alert warning">
                    <FaExclamationTriangle />
                    <div>
                      <strong>Action Required:</strong>
                      <ul>
                        <li>Implement water conservation measures immediately</li>
                        <li>Consider rainwater harvesting systems</li>
                        <li>Restrict groundwater extraction for non-essential uses</li>
                        <li>Monitor water levels more frequently</li>
                      </ul>
                    </div>
                  </div>
                ) : historyData.trend.direction === 'improving' || historyData.trend.direction === 'significantly_improving' ? (
                  <div className="recommendation-alert success">
                    <span>✅</span>
                    <div>
                      <strong>Positive Trend:</strong>
                      <ul>
                        <li>Continue current water management practices</li>
                        <li>Share best practices with neighboring villages</li>
                        <li>Maintain monitoring to ensure sustainability</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="recommendation-alert neutral">
                    <span>ℹ️</span>
                    <div>
                      <strong>Stable Conditions:</strong>
                      <ul>
                        <li>Maintain current conservation efforts</li>
                        <li>Plan for future water security</li>
                        <li>Regular monitoring recommended</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Detailed Prediction Analysis - NEW SHOWCASE FEATURE */}
              <DetailedPredictionAnalysis village={village} historyData={historyData} />

              {/* Future Prediction Section */}
              <div className="village-prediction-section">
                <div className="prediction-header">
                  <h3><FaChartLine /> Future Water Level Prediction (2025-2026)</h3>
                  <button 
                    className="prediction-info-btn"
                    onClick={() => setShowPredictionInfo(!showPredictionInfo)}
                    title="About this prediction"
                  >
                    <FaInfoCircle /> How it works
                  </button>
                </div>
                
                {/* Prediction Info/Disclaimer Box */}
                {showPredictionInfo && (
                  <div className="prediction-disclaimer-box">
                    <div className="disclaimer-header">
                      <FaInfoCircle className="disclaimer-icon" />
                      <h4>About This Prediction System</h4>
                    </div>
                    <div className="disclaimer-content">
                      <p className="disclaimer-main">
                        <strong>Intelligent Early Warning & Prioritization Tool:</strong> This prediction 
                        helps identify areas needing attention, supported by government data and 
                        multi-variable analysis.
                      </p>
                      
                      <div className="disclaimer-grid">
                        <div className="disclaimer-section">
                          <h5>📊 Data Sources</h5>
                          <ul>
                            <li>Historical trends (2021-2024)</li>
                            <li>Rainfall data (Central Ground Water Board)</li>
                            <li>Weather forecasts (7-day)</li>
                            <li>Extraction rates</li>
                          </ul>
                        </div>
                        
                        <div className="disclaimer-section">
                          <h5>✅ Best For</h5>
                          <ul>
                            <li>Identifying risk areas</li>
                            <li>Resource prioritization</li>
                            <li>Trend analysis</li>
                            <li>Planning support</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="disclaimer-warning">
                        <strong>⚠️ Important:</strong> Best used in combination with expert review 
                        and field verification for decision-making. This is a decision support tool, 
                        not a standalone decision maker.
                      </div>
                    </div>
                  </div>
                )}

                {loadingPrediction ? (
                  <div className="prediction-loading">
                    <div className="loading-spinner"></div>
                    <p>Analyzing trends and generating predictions...</p>
                  </div>
                ) : showPrediction && predictionData ? (
                  <div className="prediction-content">
                    {/* Prediction Summary */}
                    <div className="prediction-summary">
                      <div className="prediction-card">
                        <span className="prediction-year">2025</span>
                        <span className="prediction-value">{predictionData.predictions[0].predictedWaterLevel}m</span>
                        <span 
                          className="prediction-category"
                          style={{ backgroundColor: getCategoryColor(predictionData.predictions[0].category) }}
                        >
                          {predictionData.predictions[0].category}
                        </span>
                        <span className="prediction-confidence">
                          {predictionData.predictions[0].confidence}% confidence
                        </span>
                      </div>

                      <div className="prediction-card">
                        <span className="prediction-year">2026</span>
                        <span className="prediction-value">{predictionData.predictions[1].predictedWaterLevel}m</span>
                        <span 
                          className="prediction-category"
                          style={{ backgroundColor: getCategoryColor(predictionData.predictions[1].category) }}
                        >
                          {predictionData.predictions[1].category}
                        </span>
                        <span className="prediction-confidence">
                          {predictionData.predictions[1].confidence}% confidence
                        </span>
                      </div>
                    </div>

                    {/* Prediction Insights */}
                    <div className="prediction-insights">
                      <h4><FaChartBar /> Prediction Analysis</h4>
                      <div className="insight-grid">
                        <div className="insight-item">
                          <span className="insight-label">Trend Direction:</span>
                          <span className={`insight-value trend-${predictionData.insights.trend}`}>
                            {predictionData.insights.trend === 'worsening' ? '↗ Worsening' : 
                             predictionData.insights.trend === 'improving' ? '↘ Improving' : '→ Stable'}
                          </span>
                        </div>
                        <div className="insight-item">
                          <span className="insight-label">Risk Level:</span>
                          <span className={`insight-value risk-${predictionData.insights.riskLevel}`}>
                            {predictionData.insights.riskLevel === 'high' ? '● High Risk' : 
                             predictionData.insights.riskLevel === 'moderate' ? '● Moderate' : '● Low Risk'}
                          </span>
                        </div>
                        <div className="insight-item">
                          <span className="insight-label">Avg Yearly Change:</span>
                          <span className="insight-value">
                            {predictionData.historicalTrend.avgYearlyChange > 0 ? '+' : ''}
                            {predictionData.historicalTrend.avgYearlyChange}m/year
                          </span>
                        </div>
                        <div className="insight-item">
                          <span className="insight-label">Extraction Impact:</span>
                          <span className={`insight-value impact-${predictionData.insights.extractionImpact}`}>
                            {predictionData.insights.extractionImpact === 'negative' ? '▼ Negative' : 
                             predictionData.insights.extractionImpact === 'positive' ? '▲ Positive' : '■ Neutral'}
                          </span>
                        </div>
                        <div className="insight-item">
                          <span className="insight-label">Rainfall Impact:</span>
                          <span className={`insight-value impact-${predictionData.insights.rainfallImpact}`}>
                            {predictionData.insights.rainfallImpact === 'negative' ? '▼ Negative' : 
                             predictionData.insights.rainfallImpact === 'positive' ? '▲ Positive' : '■ Neutral'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Prediction Warning/Recommendation */}
                    {predictionData.insights.riskLevel === 'high' && (
                      <div className="prediction-warning">
                        <FaExclamationTriangle />
                        <div>
                          <strong>⚠️ High Risk Alert:</strong>
                          <p>
                            Based on current trends and environmental factors, this village is predicted to reach 
                            <strong> {predictionData.predictions[1].category}</strong> status by 2026. 
                            Immediate intervention is recommended to prevent further depletion.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="prediction-methodology">
                      <details>
                        <summary>ℹ️ How is this calculated?</summary>
                        <p>
                          Our prediction model uses multiple factors:
                        </p>
                        <ul>
                          <li><strong>Historical Trend Analysis:</strong> {predictionData.historicalTrend.dataPoints} years of data</li>
                          <li><strong>Groundwater Extraction:</strong> District-level extraction rates (Factor: {predictionData.environmentalFactors.extractionFactor}x)</li>
                          <li><strong>Rainfall Patterns:</strong> Regional rainfall data (Factor: {predictionData.environmentalFactors.rainfallFactor}x)</li>
                          <li><strong>Linear Regression:</strong> Statistical trend projection</li>
                        </ul>
                        <p className="methodology-note">
                          Predictions are estimates based on current conditions. Actual outcomes may vary based on 
                          conservation efforts, climate changes, and policy interventions.
                        </p>
                      </details>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default VillageDetailModal;